import type { OperationVariables, TypedDocumentNode } from '@apollo/client'
import { useEffect } from 'react'
import { match } from 'ts-pattern'
import { useWsConnectionStatus } from 'src/stores/websocket'
import { taskParameterFieldsForRailRender } from '../helpers/taskParams'
import { baseApolloClient, cache } from './client'
import type {
  ListMyTasksQueryVariables,
  ListUserTasksQueryVariables,
  Task,
} from './generated/graphql'
import {
  CancelGenerationTaskDocument,
  CreateArtworkFromTaskV2Document,
  CreateGenerationTaskDocument,
  CreateGrowthEventTaskDocument,
  CreateTrainingTaskDocument,
  DeleteGenerationTaskDocument,
  GetMyTaskStatsDocument,
  GetTaskByIdDocument,
  ListMyTasksDocument,
  ListMyTasksTypedDocument,
  ListTaskSummariesDocument,
  ListUserTaskSummariesDocument,
  ListUserTasksDocument,
  ListUserTasksTypedDocument,
  RerunGenerationTaskDocument,
  UpdateGenerationTaskDocument,
} from './generated/graphql'
import {
  createFetchQueryHelper,
  createMutationHelper,
  createQueryHook,
  infiniteQueryHook,
  modifyQueryHook,
  pagedQueryHook,
} from './utils'

const useListMyTasks = modifyQueryHook(createQueryHook(ListMyTasksDocument), {
  transform: (args: ListMyTasksQueryVariables) => ({
    ...args,
    parameterFields: args.parameterFields ?? taskParameterFieldsForRailRender,
  }),
  reduce: r => r?.me?.tasks,
})

export const useInfinityListMyTasks = infiniteQueryHook(useListMyTasks)

export const fetchListMyTasks = createFetchQueryHelper(ListMyTasksDocument)

export const fetchMyTaskStats = createFetchQueryHelper(GetMyTaskStatsDocument)

export const useListUserTasks = modifyQueryHook(
  createQueryHook(ListUserTasksDocument),
  {
    transform: (args: ListUserTasksQueryVariables) => ({
      ...args,
      parameterFields: args.parameterFields ?? taskParameterFieldsForRailRender,
    }),
    reduce: r => r?.tasks,
  },
)

export const usePagedListUserTasks = pagedQueryHook(useListUserTasks)
export const useInfinityListUserTasks = infiniteQueryHook(useListUserTasks)

export const fetchListUserTasks = createFetchQueryHelper(ListUserTasksDocument)

// Typed-parameters mirror of the legacy list hooks — remove once all
// consumers migrate. Schema docstring on `TypedParameters` has the why.
const useListMyTasksTyped = modifyQueryHook(
  createQueryHook(ListMyTasksTypedDocument),
  { reduce: r => r?.me?.tasks },
)

export const useInfinityListMyTasksTyped =
  infiniteQueryHook(useListMyTasksTyped)

export const fetchListMyTasksTyped = createFetchQueryHelper(
  ListMyTasksTypedDocument,
)

export const useListUserTasksTyped = modifyQueryHook(
  createQueryHook(ListUserTasksTypedDocument),
  { reduce: r => r?.tasks },
)

export const usePagedListUserTasksTyped = pagedQueryHook(useListUserTasksTyped)
export const useInfinityListUserTasksTyped = infiniteQueryHook(
  useListUserTasksTyped,
)

export const fetchListUserTasksTyped = createFetchQueryHelper(
  ListUserTasksTypedDocument,
)

export const useListTaskSummaries = modifyQueryHook(
  createQueryHook(ListTaskSummariesDocument),
  {
    reduce: r => r?.taskSummaries,
  },
)

// no-cache: bypass relayStylePagination which merges all pages and ignores cursor args
const createNoCachePagedHook = <
  TData,
  TVariables extends OperationVariables,
  TReduced,
>(
  document: TypedDocumentNode<TData, TVariables>,
  reduce: (r: TData | undefined) => TReduced,
) =>
  modifyQueryHook(
    createQueryHook(document, { fetchPolicy: 'no-cache' } as any),
    { reduce },
  )

export const usePagedListTaskSummaries = createNoCachePagedHook(
  ListTaskSummariesDocument,
  r => r?.taskSummaries,
)

export const useInfinityListTaskSummaries =
  infiniteQueryHook(useListTaskSummaries)

export const useListUserTaskSummaries = modifyQueryHook(
  createQueryHook(ListUserTaskSummariesDocument),
  {
    reduce: r => r?.user?.taskSummaries,
  },
)

export const usePagedListUserTaskSummaries = createNoCachePagedHook(
  ListUserTaskSummariesDocument,
  r => r?.user?.taskSummaries,
)

export const useInfinityListUserTaskSummaries = infiniteQueryHook(
  useListUserTaskSummaries,
)

export const DefaultTaskPollInterval = 5e3

const useGetTaskById = modifyQueryHook(createQueryHook(GetTaskByIdDocument), {
  transform: (id: string) => ({ id }),
  reduce: r => r?.task,
})

export const useTaskById = (
  variables: Parameters<typeof useGetTaskById>[0],
  overrideOptions?: Parameters<typeof useGetTaskById>[1],
) => {
  const taskId = typeof variables === 'string' ? variables : undefined
  const websocketConnected = useWsConnectionStatus({ detailed: false })
  const result = useGetTaskById(variables, {
    ...overrideOptions,
  })
  useEffect(() => {
    if (taskId && result.data && !websocketConnected) {
      const status = result.data.status
      match(status)
        .with('running', 'waiting', () => {
          result.startPolling(DefaultTaskPollInterval)
        })
        .with('completed', 'failed', 'cancelled', () => {
          result.stopPolling()
        })
        .otherwise(() => {
          // do nothing
        })
    }
  }, [taskId, result, websocketConnected])
  return result
}

export const updateGetTaskById = (id: string, newData: Task) => {
  cache.updateQuery(
    {
      query: GetTaskByIdDocument,
      variables: { id: id },
    },
    () => {
      return { task: { __typename: 'Task' as const, ...newData } }
    },
  )
}
export const refetchGetTaskById = (id: string) => {
  baseApolloClient.refetchQueries({
    updateCache(cache) {
      cache.evict({
        id: cache.identify({ __typename: 'Task' as const, id }),
      })
    },
  })
}
// Task updates are now handled by usePersonalEventsSync in subscriptions.ts
// which directly updates the Apollo cache

export const fetchGetTaskById = createFetchQueryHelper(GetTaskByIdDocument)

export const [useCreateGenerationTask, executeCreateGenerationTask] =
  createMutationHelper(CreateGenerationTaskDocument)

export const [useCreateArtworkFromTaskV2, executeCreateArtworkFromTaskV2] =
  createMutationHelper(CreateArtworkFromTaskV2Document)

export const [useCancelGenerationTask, executeCancelGenerationTask] =
  createMutationHelper(CancelGenerationTaskDocument)

export const [useDeleteGenerationTask, executeDeleteGenerationTask] =
  createMutationHelper(DeleteGenerationTaskDocument)

export const [useRerunGenerationTask, executeRerunGenerationTask] =
  createMutationHelper(RerunGenerationTaskDocument)

export const [useUpdateGenerationTask, executeUpdateGenerationTask] =
  createMutationHelper(UpdateGenerationTaskDocument)

export const [useCreateTrainingTask, executeCreateTrainingTask] =
  createMutationHelper(CreateTrainingTaskDocument)

export const [useCreateGrowthEventTask, executeCreateGrowthEventTask] =
  createMutationHelper(CreateGrowthEventTaskDocument)
